<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="cs-keyword-import-modal"
      dialog-width="40rem"
      dialog-max-height="40rem"
    >
      <template #header>
        {{ ls({ ja: '列の編集', en: 'Column Setting' }) }}
      </template>
      <div class="jps-modal-content">
        <BaseTab :tab-options="tabOptions" v-model="tabIndex" v-if="tabIndex">
          <BaseTabPanel val="common">
            <table class="jps-table">
              <thead>
                <tr>
                  <td style="width: 33%">項目名</td>
                  <td>{{ lss('説明', 'Description') }}</td>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in commonColumns" :key="f.key">
                  <td>
                    <button
                      class="jps-button jps-fullwidth"
                      @click="selectCommon(f)"
                    >
                      {{ ls(f.header) }}
                    </button>
                  </td>
                  <td>{{ ls(f.description) }}</td>
                </tr>
              </tbody>
            </table>
          </BaseTabPanel>
          <BaseTabPanel val="customize">
            <ValidationForm
              :rule="rule"
              :target="columnValue"
              only="header"
              @valid="(v) => headerValid = v"
            >
              <div class="jps-field">
                <label class="jps-label">この列に表示する項目</label>
                <div class="jps-tag-list jps-block">
                  <!--表示-->
                  <UiDbField
                    class="jps-tag jps-dark jps-tag-ricon"
                    v-for="(f, ind) in columnValue.fields"
                    :field="f"
                    :key="ind"
                    :deletable="true"
                    @delete="removeFromArray(columnValue.fields, f)"
                  />
                  <button
                    type="button"
                    class="jps-tag"
                    @click.stop="selectKeywordFields()"
                  >
                    項目を追加する
                  </button>
                </div>
                <div class="jps-message jps-red" v-if="zeroField">
                  {{ ls(zeroField) }}
                </div>
              </div>
            </ValidationForm>
          </BaseTabPanel>
        </BaseTab>
        <UiDbFieldSelectModal ref="dbFieldSelectModal" />
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()" :disabled="!valid">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal, UiDbFieldSelectModal } from '#components'
import type { TabOption } from '~/components/base/BaseTab.vue'

const { ls } = useI18n()
const { toCSColumn, systemCommonSearchColumn } = useCsEditor()

interface Props {
  editstate?: CsEditState,
  column?: CustomSearchColumn
}
const props = withDefaults(defineProps<Props>(),{
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _editstate: Ref<CsEditState> = ref()
const _column: Ref<CustomSearchColumn> = ref()
const headerValid: Ref<boolean> = ref(false)
const columnValue: Ref<CustomSearchColumn> = ref(null)
const openAwait = async (options?: Props) => {
  headerValid.value = false

  setOptionsValue(_editstate, 'editstate', props, options)
  setOptionsValue(_column, 'column', props, options)

  //field valueの初期化
  let f: CustomSearchColumn = {}
  if (_column.value) {
    f = clone(_column.value)
    tabIndex.value = 'customize'
  } else {
    tabIndex.value = 'common'
  }
  f.type = 'field'
  if (!f.fields) f.fields = []
  if (!f.header) f.header = { ja: '', en: '' }
  if (!f.codeKey) f.codeKey = null
  columnValue.value = f

  return await modal.value.openAwait<CustomSearchColumn>()
}
defineExpose({ openAwait })

const rule = CUSTOM_SEARCH_COLUMN_RULE
const commonColumns = systemCommonSearchColumn()

const tabIndex: Ref<string> = ref()
const tabOptions = computed((): TabOption[] => {
  //TabHeader
  if (_column.value) {
    return [
      { val: 'customize', label: ls({ ja: 'カスタマイズ', en: 'Customize' }) },
    ]
  } else {
    return [
      {
        val: 'common',
        label: ls({ ja: '汎用的な列設定', en: 'Common Field' }),
      },
      { val: 'customize', label: ls({ ja: 'カスタマイズ', en: 'Customize' }) },
    ]
  }
})

const valid = computed(() => {
  return headerValid.value && !zeroField.value
})

const zeroField = computed(() => {
  if (columnValue.value && columnValue.value.fields.length === 0) {
    return {
      ja: '検索対象項目が指定されていません。一つ以上の項目を指定してください。',
      en: 'Select one or more fields',
    }
  } else {
    return null
  }
})

const selectCommon = (f) => {
  modal.value.resolve(toCSColumn(f))
}

const confirm = () => {
  let f = normalizeEmpty(columnValue.value)
  modal.value.resolve(f)
}

const dbFieldSelectModal = ref<InstanceType<typeof UiDbFieldSelectModal>>()
const selectKeywordFields = async () => {
  try {
    const keyword = await dbFieldSelectModal.value.openAwait({
      fields: columnValue.value.fields,
      indexType: ['string'],
      database: [_editstate.value?.db.id],
      queryFilter: (q: SearchQuery) => {
        if (_editstate.value.db) {
          q.filter = {
            database: [_editstate.value.db.id],
          }
        } else {
          q.filter = {
            database: ['-default'],
          }
        }
        return q
      },
    })
    columnValue.value.fields = keyword
  } catch (error) {}
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.cs-keyword-import-modal {
  @include box-shadow;

  .jps-modal-content {
    height: 100%;
    display: flex;
  }
}
</style>
